import { tr } from "./i18n.js";
/**
 * Display helpers for the fact-check panel (Article page).
 *
 * Proposed fixes (2026-09-24, backend/fact_check_fixes.py): every
 * `unsupported` or `contradicted` claim can carry `original`,
 * `proposed` and `fix_status`:
 *   "proposed"  — can be placed now → show an Accept button
 *   "unmatched" — the passage couldn't be located exactly once → show
 *                 the suggestion, but the editor applies it by hand
 *   "applied"   — accepted → show an "Applied" badge
 * `partial` claims can carry a fix too since 2026-10-01 (except a
 * contradiction the backend auto-downgraded to partial — it arrives
 * with no fix fields). `supported` claims never get one, and runs from
 * before this shipped have none of these fields.
 *
 * Pure and framework-free — node --test frontend/scripts/factCheckView.test.mjs
 */

const FIXABLE = new Set(["partial", "unsupported", "contradicted"]);

/** "accept" | "applied" | "manual" | null */
export function fixState(claim) {
  if (!claim || !FIXABLE.has((claim.verdict || "").toLowerCase())) return null;
  if (!(claim.original || "").trim() || !(claim.proposed || "").trim()) return null;
  if (claim.fix_status === "applied") return "applied";
  if (claim.fix_status === "proposed") return "accept";
  return "manual";
}

export const LOCATION_LABEL = {
  get article() { return tr("Article"); },
  get headline() { return tr("Headline"); },
  get facebook() { return tr("Facebook post"); },
  get instagram() { return tr("Instagram post"); },
};

/** Badge text for where the claim lives, or "" for the article body. */
export function locationBadge(claim) {
  const loc = (claim && claim.location) || "article";
  return loc === "article" ? "" : LOCATION_LABEL[loc] || "";
}

/** "headline · article · Facebook · Instagram" line for a run, or "" for
 *  old runs. Runs from before 2026-09-28 never list the headline. */
export function checkedLabel(entry) {
  if (!entry || !Array.isArray(entry.social_checked)) return "";
  const parts = entry.social_checked.includes("headline")
    ? ["headline", "article"]
    : ["article"];
  for (const loc of entry.social_checked) {
    if (loc === "facebook") parts.push("Facebook");
    else if (loc === "instagram") parts.push("Instagram");
  }
  return parts.join(" · ");
}

// Editor-supplied sources (2026-09-25, backend/fact_check_editor_sources.py).
// A run's `sources_scraped` rows carry `kind`: "url" for scraped pages
// (rows from older runs have no kind — treat them as "url"), "notes"
// for the Notes box, "document" for an attached document. A claim's
// `source_url` can be `editor://notes` or `editor://document/<n>`,
// which is not a link.
const isEditorUrl = (u) => typeof u === "string" && u.startsWith("editor://");

/**
 * Chip data for one run, or null when the run has no sources at all.
 *   { ok, total, clean, editor }
 * `ok`/`total` count scraped URLs only (total 0 → no URL chip);
 * `editor` is "" or e.g. "notes · 2 documents".
 */
export function sourcesSummary(entry) {
  const rows = (entry && Array.isArray(entry.sources_scraped) && entry.sources_scraped) || [];
  const urls = rows.filter((r) => !r.kind || r.kind === "url");
  const notes = rows.filter((r) => r.kind === "notes").length;
  const docs = rows.filter((r) => r.kind === "document").length;
  const parts = [];
  if (notes) parts.push("notes");
  if (docs) parts.push(`${docs} document${docs === 1 ? "" : "s"}`);
  if (!urls.length && !parts.length) return null;
  const ok = urls.filter((r) => r.status === "ok").length;
  return { ok, total: urls.length, clean: ok === urls.length, editor: parts.join(" · ") };
}

/**
 * How to show a claim's source: { href, text }. `href` is null for
 * editor material (shown as a plain label, never a broken link) and for
 * a missing source_url (text "" → render nothing).
 */
export function sourceDisplay(claim, entry) {
  const url = ((claim && claim.source_url) || "").trim();
  if (!url) return { href: null, text: "" };
  if (!isEditorUrl(url)) return { href: url, text: url };
  const row = ((entry && entry.sources_scraped) || []).find((r) => r && r.url === url);
  if (row && row.label) return { href: null, text: row.label };
  return {
    href: null,
    text: url === "editor://notes" ? tr("Editor's notes") : tr("Attached document"),
  };
}

/** Warning for documents that were used but never stored (older articles). */
export function missingDocsLabel(entry) {
  const n = Number((entry && entry.editor_docs_missing) || 0);
  if (!n) return "";
  return n === 1
    ? tr("1 attached document not checked — re-attach it and regenerate to check against it")
    : tr("{n} attached documents not checked — re-attach them and regenerate to check against them", { n });
}

/** Tooltip for a score the backend estimated because the model left
 *  `score` out of its reply (2026-10-01), or "" for a normal run. */
export function scoreNote(entry) {
  return entry && entry.score_estimated
    ? tr("Estimated from the flagged claims — the model didn't return a score on this run.")
    : "";
}

/** Count of fixes still waiting for the editor in one run. */
export function pendingFixCount(entry) {
  return ((entry && entry.flagged_claims) || []).filter(
    (c) => fixState(c) === "accept",
  ).length;
}

/** Success toast after Accept — reminds where the change still has to go. */
export function appliedMessage(claim, article) {
  const loc = (claim && claim.location) || "article";
  if (loc === "article") {
    return article && article.wordpress_post_id
      ? tr("Fix applied — click “Update on WordPress” to push it to the site")
      : tr("Fix applied to the article");
  }
  if (loc === "headline") {
    return article && article.wordpress_post_id
      ? tr("Fix applied to the headline — click “Update on WordPress” to push it to the site")
      : tr("Fix applied to the headline");
  }
  const label = LOCATION_LABEL[loc] || "post";
  const posted = ((article && article.social_posts) || []).some(
    (p) => p && p.platform === loc,
  );
  return posted
    ? tr("Fix applied to the {label} copy. Already-published posts on {value} aren't changed — edit them there.", { label, value: loc === "facebook" ? tr("Facebook") : tr("Instagram") })
    : tr("Fix applied to the {label} copy", { label });
}

/** Toast after undoing an accepted fix (2026-10-12). */
export function undoneMessage(claim, article) {
  const loc = (claim && claim.location) || "article";
  const where =
    loc === "article"
      ? tr("the article")
      : loc === "headline"
        ? tr("the headline")
        : `the ${LOCATION_LABEL[loc] || "post"} copy`;
  const onWp =
    (loc === "article" || loc === "headline") &&
    article && article.wordpress_post_id;
  return onWp
    ? tr("Change undone in {where} — click “Update on WordPress” to push it to the site", { where })
    : tr("Change undone in {where}", { where });
}

// Pin-to-side preference. Per-browser convenience only: storage can be
// unavailable (private mode, blocked site data), so every access is
// guarded and the default is "not pinned".
export const PIN_STORAGE_KEY = "newsdesk.factCheckPinned";

export function readPinned(storage) {
  try {
    return (storage || globalThis.localStorage)?.getItem(PIN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writePinned(value, storage) {
  try {
    (storage || globalThis.localStorage)?.setItem(PIN_STORAGE_KEY, value ? "1" : "0");
  } catch {
    /* best effort */
  }
}